import React, { createContext, useContext, useEffect, useState } from 'react';
import { signIn, signOut, getCurrentUser, fetchAuthSession } from '@aws-amplify/auth';
import { microsoftAuthService } from '../services/microsoftAuth';
import { isDevelopmentMode, skipAwsConfig } from '../config/aws-config';

interface User {
  username: string;
  email: string;
  attributes: any;
  microsoftAccount?: any;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  signUp: (username: string, password: string, email: string, givenName?: string, familyName?: string) => Promise<any>;
  confirmSignUp: (username: string, code: string) => Promise<any>;
  signInWithMicrosoft: () => Promise<any>;
  linkMicrosoftAccount: () => Promise<any>;
  isAuthenticated: boolean;
  hasMicrosoftAccount: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      // In development mode without AWS, create a mock user
      if (skipAwsConfig && isDevelopmentMode) {
        setUser({
          username: 'dev-user',
          email: 'dev@example.com',
          attributes: { email: 'dev@example.com' },
        });
        setLoading(false);
        return;
      }

      await microsoftAuthService.initialize();
      await checkAuthState();
    } catch (error) {
      console.error('Error initializing auth:', error);
      setLoading(false);
    }
  };

  const checkAuthState = async () => {
    try {
      // Skip AWS auth in development mode
      if (skipAwsConfig && isDevelopmentMode) {
        return;
      }

      const currentUser = await getCurrentUser();
      const microsoftAccount = microsoftAuthService.getCurrentAccount();
      
      setUser({
        username: currentUser.username,
        email: currentUser.signInDetails?.loginId || '',
        attributes: currentUser,
        microsoftAccount,
      });
    } catch (error) {
      // Check if user is authenticated with Microsoft only
      const microsoftAccount = microsoftAuthService.getCurrentAccount();
      if (microsoftAccount) {
        setUser({
          username: microsoftAccount.username,
          email: microsoftAccount.username,
          attributes: microsoftAccount,
          microsoftAccount,
        });
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (username: string, password: string) => {
    try {
      // Skip AWS auth in development mode
      if (skipAwsConfig && isDevelopmentMode) {
        // Mock successful sign-in for development
        setUser({
          username: username,
          email: username,
          attributes: { email: username },
        });
        return { isSignedIn: true };
      }

      const result = await signIn({ username, password });
      await checkAuthState();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      if (user?.microsoftAccount) {
        await microsoftAuthService.signOut();
      }
      await signOut();
      setUser(null);
    } catch (error) {
      throw error;
    }
  };

  const handleSignUp = async (username: string, password: string, email: string, givenName?: string, familyName?: string) => {
    try {
      // Skip AWS auth in development mode
      if (skipAwsConfig && isDevelopmentMode) {
        // Mock successful signup for development
        return { isSignUpComplete: false, nextStep: { signUpStep: 'CONFIRM_SIGN_UP' } };
      }

      const { signUp } = await import('@aws-amplify/auth');
      const result = await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
            given_name: givenName || '',
            family_name: familyName || '',
          },
        },
      });
      return result;
    } catch (error) {
      throw error;
    }
  };

  const handleConfirmSignUp = async (username: string, code: string) => {
    try {
      // Skip AWS auth in development mode
      if (skipAwsConfig && isDevelopmentMode) {
        // Mock successful confirmation for development
        return { isSignUpComplete: true };
      }

      const { confirmSignUp } = await import('@aws-amplify/auth');
      const result = await confirmSignUp({
        username,
        confirmationCode: code,
      });
      return result;
    } catch (error) {
      throw error;
    }
  };

  const signInWithMicrosoft = async () => {
    try {
      const result = await microsoftAuthService.signInWithPopup();
      await checkAuthState();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const linkMicrosoftAccount = async () => {
    try {
      if (!user) {
        throw new Error('User must be authenticated to link Microsoft account');
      }
      
      const result = await microsoftAuthService.signInWithPopup();
      await checkAuthState();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn: handleSignIn,
    signOut: handleSignOut,
    signUp: handleSignUp,
    confirmSignUp: handleConfirmSignUp,
    signInWithMicrosoft,
    linkMicrosoftAccount,
    isAuthenticated: !!user,
    hasMicrosoftAccount: !!user?.microsoftAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};